import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container, Row, Col, Card, Button, Form,
  Alert, Spinner, InputGroup, OverlayTrigger, Tooltip
} from 'react-bootstrap';
import {
  FaFileContract, FaSearch, FaCalendarAlt, FaWhatsapp,
  FaEye, FaDownload, FaExclamationTriangle, FaClock,
  FaSort, FaSortUp, FaSortDown, FaFilter, FaTimes,
  FaCheckCircle, FaSkullCrossbones
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { PageHeader } from '../../components/shared/PageHeader';
import { ConfirmModal } from '../../components/shared/ConfirmModal';

// ── Utils ──
const formatCurrency = (v) =>
  typeof v === 'number' && !isNaN(v)
    ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : 'R$ 0,00';

const formatDate = (s) => {
  if (!s) return '-';
  try {
    const [y, m, d] = s.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
  } catch { return '-'; }
};

const limparTelefone = (tel) => (tel || '').replace(/\D/g, '');

// ── Pílula custom ──
const Pilula = ({ children, color = '#6c757d', icon: Icon }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 9px', borderRadius: 12, fontSize: 11,
    fontWeight: 600, lineHeight: 1.4,
    backgroundColor: `${color}1f`, color,
    border: `1px solid ${color}40`, whiteSpace: 'nowrap',
  }}>
    {Icon && <Icon size={9} />}
    {children}
  </span>
);

// ── Cor + label por urgência ──
const getUrgencia = (dias) => {
  if (dias < 0)   return { color: '#6f1d1d', label: `${Math.abs(dias)}d vencido`, icon: FaSkullCrossbones };
  if (dias === 0) return { color: '#dc3545', label: 'Vence hoje',                 icon: FaExclamationTriangle };
  if (dias <= 7)  return { color: '#dc3545', label: `${dias} dia(s)`,             icon: FaClock };
  if (dias <= 15) return { color: '#f8961e', label: `${dias} dia(s)`,             icon: FaClock };
  if (dias <= 30) return { color: '#ffc107', label: `${dias} dia(s)`,             icon: FaClock };
  return            { color: '#198754', label: `${dias} dia(s)`,                  icon: FaClock };
};

// ==========================================
export default function ContratosExpirando() {
  const navigate = useNavigate();

  const [data,         setData]         = useState([]);
  const [resumo,       setResumo]       = useState(null);
  const [daysAhead,    setDaysAhead]    = useState(30);
  const [filtroSituacao, setFiltroSituacao] = useState('todos'); // 🆕 todos | vencidos | vencendo
  const [searchTerm,   setSearchTerm]   = useState('');
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [sortConfig,   setSortConfig]   = useState({ key: 'diasRestantes', direction: 'asc' });

  // 🆕 Modal finalizar
  const [showFinalizarModal, setShowFinalizarModal] = useState(false);
  const [contratoFinalizar,  setContratoFinalizar]  = useState(null);
  const [finalizando,        setFinalizando]        = useState(false);

  // ── Fetch ──
const fetchData = useCallback(async () => {
  try {
    setLoading(true); setError('');
    const params = { daysAhead, includeOverdue: true };
    const res = await api.get('/reports/contracts/expiring', { params });

    // 🛡️ Blindagem
    setData(Array.isArray(res.data?.data) ? res.data.data : []);
    setResumo(res.data?.resumo || {
      total: 0, vencidos: 0, vencendo7: 0, vencendo15: 0,
      vencendo30: 0, valorTotal: 0, totalAReceber: 0, valorVencido: 0,
    });
  } catch (err) {
    console.error('[ContratosExpirando]', err);
    setError(err.response?.data?.error || 'Erro ao carregar relatório.');
    setData([]);     // 🛡️ não deixa undefined
    setResumo(null);
  } finally {
    setLoading(false);
  }
}, [daysAhead]);


  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Filtro local + Ordenação ──
  const filteredData = useMemo(() => {
    let list = data;

    // Filtro situação
    if (filtroSituacao === 'vencidos')  list = list.filter(d => d.isVencido);
    if (filtroSituacao === 'vencendo')  list = list.filter(d => !d.isVencido);

    // Busca
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      list = list.filter(d =>
        (d.cliente.nome || '').toLowerCase().includes(t) ||
        (d.plano || '').toLowerCase().includes(t) ||
        (d.numeroContrato || '').toLowerCase().includes(t)
      );
    }

    // Ordenação
    if (sortConfig.key) {
      list = [...list].sort((a, b) => {
        let av, bv;
        switch (sortConfig.key) {
          case 'cliente':   av = a.cliente.nome; bv = b.cliente.nome; break;
          case 'endDate':
          case 'startDate': av = new Date(a[sortConfig.key]); bv = new Date(b[sortConfig.key]); break;
          default:          av = a[sortConfig.key]; bv = b[sortConfig.key];
        }
        if (av < bv) return sortConfig.direction === 'asc' ? -1 : 1;
        if (av > bv) return sortConfig.direction === 'asc' ?  1 : -1;
        return 0;
      });
    }
    return list;
  }, [data, filtroSituacao, searchTerm, sortConfig]);

  const handleSort = (key) => setSortConfig(prev => ({
    key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <FaSort className="ms-1 text-muted" />;
    return sortConfig.direction === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };

  const limparFiltros = () => {
    setDaysAhead(30); setSearchTerm(''); setFiltroSituacao('todos');
  };

  const abrirWhatsapp = (cliente, isVencido) => {
    const tel = limparTelefone(cliente.phone);
    if (!tel) return toast.warn('Cliente sem telefone cadastrado.');
    const msg = encodeURIComponent(
      isVencido
        ? `Olá ${cliente.nome}! Seu contrato venceu. Vamos renovar?`
        : `Olá ${cliente.nome}! Seu contrato está próximo do vencimento. Vamos renovar?`
    );
    window.open(`https://wa.me/55${tel}?text=${msg}`, '_blank');
  };

  // 🆕 Finalizar contrato
  const confirmarFinalizar = (item) => {
    setContratoFinalizar(item);
    setShowFinalizarModal(true);
  };

  const handleFinalizar = async () => {
    if (!contratoFinalizar) return;
    try {
      setFinalizando(true);
      await api.patch(`/orders/${contratoFinalizar.orderId}/complete`);
      toast.success('Contrato finalizado com sucesso!');
      setShowFinalizarModal(false);
      setContratoFinalizar(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao finalizar contrato.');
    } finally {
      setFinalizando(false);
    }
  };

  const exportCSV = () => {
    const rows = [
      ['Nº Contrato','Cliente','Plano','Início','Fim','Situação','Dias',
       'Total Pcls','Pagas','Em Aberto','Atrasadas','Valor Total','Total Pago','À Pagar'].join(','),
      ...filteredData.map(d => [
        d.numeroContrato, `"${d.cliente.nome}"`, `"${d.plano}"`,
        formatDate(d.startDate), formatDate(d.endDate),
        d.isVencido ? 'Vencido' : 'A vencer',
        d.diasRestantes,
        d.totalParcelas, d.parcelasPagas, d.parcelasEmAberto, d.parcelasAtrasadas,
        d.valorTotal.toFixed(2), d.totalPago.toFixed(2), d.aPagar.toFixed(2),
      ].join(','))
    ].join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([rows], { type: 'text/csv;charset=utf-8;' }));
    a.download = `contratos_vencendo_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <Container fluid className="p-4">

      <PageHeader
        title="Contratos Próximos a Vencer"
        subtitle={<><FaFileContract className="me-1" />
          {filteredData.length} contrato(s) — próximos {daysAhead} dias + vencidos
        </>}
      >
        <Button variant="outline-secondary" onClick={exportCSV}
          disabled={filteredData.length === 0} className="me-2">
          <FaDownload className="me-2" />Exportar CSV
        </Button>
      </PageHeader>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          <FaExclamationTriangle className="me-2" />{error}
        </Alert>
      )}

      {/* ── Cards Resumo ── */}
      {resumo && (
        <Row className="mb-4">
          {[
            { label: 'Vencidos (perdidos)',  value: resumo.vencidos,      color: 'danger',  Icon: FaSkullCrossbones,    isCount: true, situacao: 'vencidos' },
            { label: 'Vencendo em 7 dias',   value: resumo.vencendo7,     color: 'warning', Icon: FaExclamationTriangle, isCount: true },
            { label: 'Vencendo em 30 dias',  value: resumo.vencendo30,    color: 'primary', Icon: FaClock,               isCount: true },
            { label: 'Valor vencido',        value: resumo.valorVencido,  color: 'danger',  Icon: FaCalendarAlt },
          ].map(({ label, value, color, Icon, isCount, situacao }) => (
            <Col lg={3} md={6} className="mb-3" key={label}>
              <Card
                className="h-100 shadow-sm"
                style={{ cursor: situacao ? 'pointer' : 'default' }}
                onClick={() => situacao && setFiltroSituacao(situacao)}
              >
                <Card.Body className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">{label}</div>
                    <h4 className={`mb-0 text-${color}`}>
                      {isCount ? value : formatCurrency(value)}
                    </h4>
                  </div>
                  <div className={`bg-${color} bg-opacity-10 p-3 rounded-circle`}>
                    <Icon className={`text-${color}`} size={20} />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* ── Filtros ── */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="bg-light py-2 d-flex justify-content-between align-items-center">
          <span className="fw-semibold"><FaFilter className="me-2" />Filtros</span>
          <Button variant="link" size="sm" className="text-muted p-0 text-decoration-none" onClick={limparFiltros}>
            <FaTimes className="me-1" />Limpar filtros
          </Button>
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={4}>
              <Form.Label className="small text-muted mb-1">Buscar</Form.Label>
              <InputGroup>
                <InputGroup.Text><FaSearch /></InputGroup.Text>
                <Form.Control placeholder="Cliente, plano ou nº do contrato..."
                  value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
              </InputGroup>
            </Col>

            <Col lg={4}>
              <Form.Label className="small text-muted mb-1">Situação</Form.Label>
              <div className="d-flex gap-1">
                {[
                  { v: 'todos',    l: 'Todos' },
                  { v: 'vencidos', l: 'Vencidos' },
                  { v: 'vencendo', l: 'A vencer' },
                ].map(op => (
                  <Button key={op.v} size="sm" className="flex-fill"
                    variant={filtroSituacao === op.v ? 'primary' : 'outline-secondary'}
                    onClick={() => setFiltroSituacao(op.v)}>
                    {op.l}
                  </Button>
                ))}
              </div>
            </Col>

            <Col lg={4}>
              <Form.Label className="small text-muted mb-1">Vencendo nos próximos</Form.Label>
              <div className="d-flex gap-1">
                {[7, 15, 30, 60, 90].map(d => (
                  <Button key={d} size="sm" className="flex-fill"
                    variant={daysAhead === d ? 'primary' : 'outline-secondary'}
                    onClick={() => setDaysAhead(d)}>
                    {d}d
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ── Tabela ── */}
      {loading ? (
        <div className="text-center mt-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <Card className="shadow-sm">
          <Card.Body>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    {[
                      ['numeroContrato','Nº Contrato'],
                      ['cliente','Cliente'],
                      ['startDate','Início'],
                      ['endDate','Fim'],
                      ['diasRestantes','Situação'],
                      ['totalParcelas','Parcelas'],
                      ['valorTotal','Valor'],
                      ['totalPago','Pago'],
                      ['aPagar','À Pagar'],
                    ].map(([key, label]) => (
                      <th key={key} style={{ cursor:'pointer' }} onClick={() => handleSort(key)}>
                        {label} <SortIcon col={key} />
                      </th>
                    ))}
                    <th className="text-end">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map(item => {
                    const urg = getUrgencia(item.diasRestantes);
                    return (
                      <tr key={item.orderId}
                        style={item.isVencido ? { backgroundColor: '#fff5f5' } : {}}>
                        <td><code>{item.numeroContrato}</code></td>
                        <td>
                          <div className="fw-semibold">{item.cliente.nome}</div>
                          <small className="text-muted">{item.plano}</small>
                        </td>
                        <td>{formatDate(item.startDate)}</td>
                        <td className="fw-semibold">{formatDate(item.endDate)}</td>
                        <td>
                          <Pilula color={urg.color} icon={urg.icon}>{urg.label}</Pilula>
                        </td>
                        <td>
                          <small>
                            <span className="text-success">{item.parcelasPagas}</span> /{' '}
                            <span className="text-warning">{item.parcelasEmAberto}</span> /{' '}
                            <span className="text-danger">{item.parcelasAtrasadas}</span>
                            <br />
                            <span className="text-muted">de {item.totalParcelas}</span>
                          </small>
                        </td>
                        <td className="fw-bold">{formatCurrency(item.valorTotal)}</td>
                        <td className="text-success">{formatCurrency(item.totalPago)}</td>
                        <td className="text-danger fw-bold">{formatCurrency(item.aPagar)}</td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end gap-1">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Ver pedido</Tooltip>}>
                              <Button size="sm" variant="outline-primary"
                                onClick={() => navigate(`/pedidos/${item.orderId}`)}>
                                <FaEye />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Enviar WhatsApp</Tooltip>}>
                              <Button size="sm" variant="outline-success"
                                onClick={() => abrirWhatsapp(item.cliente, item.isVencido)}>
                                <FaWhatsapp />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Finalizar contrato</Tooltip>}>
                              <Button size="sm" variant="outline-dark"
                                onClick={() => confirmarFinalizar(item)}>
                                <FaCheckCircle />
                              </Button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredData.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center py-5">
                        <FaFileContract size={40} className="mb-2 opacity-25 d-block mx-auto" />
                        <p className="text-muted mb-0">
                          Nenhum contrato encontrado 🎉
                        </p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {filteredData.length > 0 && (
              <div className="mt-3 d-flex gap-3 flex-wrap small text-muted">
                <span><strong>Parcelas:</strong></span>
                <span><span className="text-success">●</span> Pagas</span>
                <span><span className="text-warning">●</span> Em aberto</span>
                <span><span className="text-danger">●</span> Atrasadas</span>
                <span className="ms-3"><strong>Linhas avermelhadas</strong> = contrato vencido</span>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* ── Modal Finalizar ── */}
      <ConfirmModal
        show={showFinalizarModal}
        onHide={() => setShowFinalizarModal(false)}
        onConfirm={handleFinalizar}
        title="Finalizar Contrato"
        message={
          <>
            Deseja marcar o contrato <strong>{contratoFinalizar?.numeroContrato}</strong> de{' '}
            <strong>{contratoFinalizar?.cliente.nome}</strong> como <strong>CONCLUÍDO</strong>?
            <br /><br />
            <small className="text-muted">
              Isso remove o contrato dessa lista. Use quando o cliente <strong>não vai renovar</strong>{' '}
              ou quando o serviço foi entregue por completo.
            </small>
          </>
        }
        confirmLabel={finalizando ? 'Finalizando...' : 'Finalizar'}
        confirmVariant="dark"
        icon={FaCheckCircle}
      />
    </Container>
  );
}
